/**
 * Auth Storage: In-Memory Access Token + Persistent Refresh Token
 *
 * SECURITY NOTES:
 * - Access token stored in memory to prevent XSS access via JS
 * - Refresh token stored in localStorage (pragmatic for SPA; assumes CSP + sanitization)
 * - If backend adds httpOnly cookie support, migrate refresh token there
 * - Apply Content Security Policy, validate all user input, keep dependencies updated
 */

import { AuthSession } from './authTypes';

const STORAGE_KEYS = {
  REFRESH_TOKEN: 'circlebe.refreshToken',
  EXPIRES_AT: 'circlebe.expiresAt',
  USER_ID: 'circlebe.userId',
  EMAIL_CONFIRMED: 'circlebe.emailConfirmed',
  PROFILE_CONFIRMED: 'circlebe.profileConfirmed',
  REMEMBER_ME: 'circlebe.rememberMe',
};

/**
 * In-memory access token storage
 * Cleared on page reload (XSS mitigation)
 */
let memoryAccessToken: string | null = null;

/**
 * Set the in-memory access token
 */
export const setMemoryAccessToken = (token: string): void => {
  memoryAccessToken = token;
};

/**
 * Get the in-memory access token
 */
export const getMemoryAccessToken = (): string | null => {
  return memoryAccessToken;
};

/**
 * Clear the in-memory access token
 */
export const clearMemoryAccessToken = (): void => {
  memoryAccessToken = null;
};

/**
 * Get whether the user chose to remain logged in
 * Defaults to true for backward compatibility with existing sessions
 */
export const getRememberMe = (): boolean => {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME);
    // Default to true when key is absent so existing sessions (stored in localStorage) keep working
    return value === null ? true : value === 'true';
  } catch (e) {
    console.error('Failed to read rememberMe from localStorage:', e);
    return true;
  }
};

/**
 * Persist the user's "remain logged in" preference
 */
export const setRememberMe = (remember: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, String(remember));
  } catch (e) {
    console.error('Failed to set rememberMe in localStorage:', e);
  }
};

/**
 * Get the refresh token from localStorage (remember me) or sessionStorage (session only)
 */
export const getRefreshToken = (): string | null => {
  try {
    return (
      localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
      sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    );
  } catch (e) {
    console.error('Failed to read refresh token from storage:', e);
    return null;
  }
};

/**
 * Set the refresh token.
 * Stored in localStorage when the user chose to remain logged in,
 * otherwise stored in sessionStorage so it is cleared when the browser is closed.
 */
export const setRefreshToken = (token: string | null): void => {
  try {
    if (token === null) {
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    } else if (getRememberMe()) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
    } else {
      sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
    }
  } catch (e) {
    console.error('Failed to set refresh token in storage:', e);
  }
};

/**
 * Get the access token expiration time from localStorage
 */
export const getExpiresAt = (): Date | null => {
  try {
    const expiresAtStr = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
    if (!expiresAtStr) return null;
    return new Date(expiresAtStr);
  } catch (e) {
    console.error('Failed to read expiresAt from localStorage:', e);
    return null;
  }
};

/**
 * Set the access token expiration time in localStorage (as ISO string)
 */
export const setExpiresAt = (date: Date | null): void => {
  try {
    if (date === null) {
      localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    } else {
      localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, date.toISOString());
    }
  } catch (e) {
    console.error('Failed to set expiresAt in localStorage:', e);
  }
};

/**
 * Get the user ID from localStorage
 */
export const getUserId = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.USER_ID);
  } catch (e) {
    console.error('Failed to read userId from localStorage:', e);
    return null;
  }
};

/**
 * Set the user ID in localStorage
 */
export const setUserId = (userId: string | null): void => {
  try {
    if (userId === null) {
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
    } else {
      localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
    }
  } catch (e) {
    console.error('Failed to set userId in localStorage:', e);
  }
};

/**
 * Get email confirmation status from localStorage
 */
export const getEmailConfirmed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEYS.EMAIL_CONFIRMED) === 'true';
  } catch (e) {
    console.error('Failed to read emailConfirmed from localStorage:', e);
    return false;
  }
};

/**
 * Set email confirmation status in localStorage
 */
export const setEmailConfirmed = (confirmed: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.EMAIL_CONFIRMED, String(confirmed));
  } catch (e) {
    console.error('Failed to set emailConfirmed in localStorage:', e);
  }
};

/**
 * Get profile confirmation status from localStorage
 */
export const getProfileConfirmed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEYS.PROFILE_CONFIRMED) === 'true';
  } catch (e) {
    console.error('Failed to read profileConfirmed from localStorage:', e);
    return false;
  }
};

/**
 * Set profile confirmation status in localStorage
 */
export const setProfileConfirmed = (confirmed: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PROFILE_CONFIRMED, String(confirmed));
  } catch (e) {
    console.error('Failed to set profileConfirmed in localStorage:', e);
  }
};

/**
 * Store the entire session (both memory and localStorage)
 */
export const setSession = (session: AuthSession): void => {
  setMemoryAccessToken(session.accessToken);
  setRefreshToken(session.refreshToken);
  setExpiresAt(session.expiresAt);
  setUserId(session.userId);
  setEmailConfirmed(session.emailConfirmed);
  setProfileConfirmed(session.profileConfirmed);
};

/**
 * Load persisted tokens from localStorage
 * Used on app initialization to restore session metadata
 * Returns null if no refresh token is found
 * Note: accessToken is empty since it's in-memory cleared on reload;
 * caller should refresh to get a new access token
 */
export const loadPersistedTokens = (): AuthSession | null => {
  const refreshToken = getRefreshToken();
  const userId = getUserId();

  if (!refreshToken || !userId) {
    return null;
  }

  return {
    userId,
    accessToken: '', // Will be populated after refresh
    expiresAt: getExpiresAt() || new Date(),
    refreshToken,
    emailConfirmed: getEmailConfirmed(),
    profileConfirmed: getProfileConfirmed(),
  };
};

/**
 * Load session from storage (memory + localStorage)
 * Returns null if any required component is missing
 */
export const loadSession = (): AuthSession | null => {
  const accessToken = getMemoryAccessToken();
  const refreshToken = getRefreshToken();
  const expiresAt = getExpiresAt();
  const userId = getUserId();
  const emailConfirmed = getEmailConfirmed();
  const profileConfirmed = getProfileConfirmed();

  if (!accessToken || !refreshToken || !expiresAt || !userId) {
    return null;
  }

  return {
    userId,
    accessToken,
    expiresAt,
    refreshToken,
    emailConfirmed,
    profileConfirmed,
  };
};

/**
 * Clear all session data from both memory and localStorage
 */
export const clearSession = (): void => {
  clearMemoryAccessToken();
  setRefreshToken(null);
  setExpiresAt(null);
  setUserId(null);
  setEmailConfirmed(false);
  setProfileConfirmed(false);
  localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
};

/**
 * Parse expiration time from backend response
 * Handles both ISO-8601 strings and epoch timestamps
 */
export const parseExpirationTime = (
  expires?: string,
  expiresAtUtc?: string,
  expiresAtEpoch?: number
): Date => {
  // Try ISO-8601 fields first
  if (expiresAtUtc) {
    return new Date(expiresAtUtc);
  }

  // Try epoch milliseconds
  if (expiresAtEpoch !== undefined) {
    return new Date(expiresAtEpoch);
  }

  // Fallback to 'expires' field (may be ISO or epoch string)
  if (expires) {
    const parsed = new Date(expires);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    // Try parsing as epoch string
    const epochNum = parseInt(expires, 10);
    if (!isNaN(epochNum)) {
      return new Date(epochNum);
    }
  }

  // Default to 1 hour from now (shouldn't reach here with valid response)
  const oneHourFromNow = new Date();
  oneHourFromNow.setHours(oneHourFromNow.getHours() + 1);
  return oneHourFromNow;
};

/**
 * Check if current access token is expired
 */
export const isAccessTokenExpired = (): boolean => {
  const expiresAt = getExpiresAt();
  if (!expiresAt) return true;
  return new Date() > expiresAt;
};

/**
 * Check if access token should be refreshed proactively
 * (within 60 seconds of expiry)
 */
export const shouldRefreshProactively = (): boolean => {
  const expiresAt = getExpiresAt();
  if (!expiresAt) return false;

  const now = new Date();
  const sixtySecondsFromNow = new Date(now.getTime() + 60 * 1000);
  return sixtySecondsFromNow > expiresAt;
};
